import { Button } from '$tablet-app/src/app/components/Button/Button';
import Checkbox from '$tablet-app/src/app/components/Checkbox';
import ScreenContainer from '$tablet-app/src/app/components/ScreenContainer';
import Spacer from '$tablet-app/src/app/components/Spacer';
import { Text } from '$tablet-app/src/app/components/Text/Text';
import { AndroidOnly } from '$tablet-app/src/app/platform';
import { color, layout, palette, spacing } from '$tablet-app/src/app/theme';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { FC } from 'react';
import {
  Image,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { shallow } from 'zustand/shallow';
import { useStateStore } from '../../stores/useStateStore';

interface AvatarSelectItemProps {
  name: string;
  description: string;
  live: boolean;
  image: any;
}

const avatars = [
  {
    name: 'Pixi',
    description:
      "Pixi is a caring house spirit who’s calm, capable and reliably helpful. Taking pride in their role as family caretaker, Pixi's unshakeable stability is a cornerstone of their personality.",
    live: true,
    image: require('../../../../assets/avatar/pegasus/Pegasus.png'),
  },
  {
    name: 'Hachikō',
    description:
      'Hachikō is a loyal and practical fellow who takes his role in the family seriously. He takes his time thinking things through because he prides himself on being solid and reliable.',
    live: false,
    image: require('../../../../assets/avatar/hachiko/Hachiko.png'),
  },
  {
    name: 'Wiz',
    description:
      'Wiz is upbeat, spontaneous and fun. She’s always keen to meet new people and explore new things. Whenever you speak to her, you get the sense she’s just back from doing something super interesting.',
    live: false,
    image: require('../../../../assets/avatar/wiz/Wiz.png'),
  },
];

const AvatarSelectScreen: FC = () => {
  const [enabled, setEnabled] = useStateStore(
    (state) => [state.wake_word_enabled, state.setWakeWordEnabled],
    shallow
  );

  return (
    <ScreenContainer style={styles.container}>
      <AndroidOnly>
        <Checkbox
          label="Voice Activation of Avatar"
          value={!!enabled}
          onChange={setEnabled}
          style={styles.voiceActivationCheckbox}
        />

        <Spacer height="1px" color="lightgrey" />
      </AndroidOnly>

      <ScrollView>
        {avatars.map((a) => (
          <AvatarSelectItem key={a.name} {...a} />
        ))}
      </ScrollView>
    </ScreenContainer>
  );
};

const AvatarSelectItem = ({
  name,
  description,
  live,
  image,
}: AvatarSelectItemProps) => {
  const { width } = useWindowDimensions();

  return (
    <View style={styles.avatarSelectItem}>
      <View style={styles.avatarImageContainer}>
        <Image style={styles.avatarSelectImage} source={image} />
      </View>

      <View
        style={[
          styles.avatarDescriptionContainer,
          { minWidth: Math.min(width - 100, 320) },
        ]}
      >
        <View
          style={[
            styles.avatarSelectItemDescription,
            { minWidth: Math.min(width - 100, 310) },
          ]}
        >
          <Text variant="h3">{name}</Text>

          <Text>{description}</Text>
        </View>

        <View style={styles.avatarSelectItemButtonContainer}>
          <Button
            preset="secondary"
            style={[styles.button, live ? styles.activeButton : undefined]}
          >
            <Text weight="semibold" style={styles.buttonText}>
              {live ? (
                <>
                  <FontAwesomeIcon
                    style={{ color: color.primary }}
                    icon="check"
                  />{' '}
                  live
                </>
              ) : (
                'Coming Soon'
              )}
            </Text>
          </Button>
        </View>
      </View>
    </View>
  );
};

// Styles
const styles = StyleSheet.create({
  voiceActivationCheckbox: {
    marginBottom: spacing[4],
  },
  avatarDescriptionContainer: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    columnGap: spacing[6],
    rowGap: spacing[2],
  },
  container: {
    flex: 1,
    padding: 16,
  },
  avatarSelectItem: {
    ...layout.row,
    paddingVertical: spacing[4],
    borderBottomColor: palette.Outline_blue,
    borderBottomWidth: 1,

    flexWrap: 'wrap',
  },
  avatarImageContainer: {
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  avatarSelectImage: {
    height: 180,
    width: 200,
  },
  avatarSelectItemDescription: {
    flex: 1,
    maxWidth: 750,
  },
  avatarSelectItemButtonContainer: {
    marginLeft: 'auto',
    justifyContent: 'flex-end',
  },
  button: { width: 170 },
  buttonText: {
    color: color.primary,
  },
  activeButton: {
    borderColor: palette.Primary_blue,
    borderWidth: 2,
  },
});

export default AvatarSelectScreen;
